import type { ActivityRecord } from '@fit-editor/fit-core'

import {
  elevationUnitLabel,
  metersPerSecondToSpeedUnit,
  metersToElevationUnit,
  speedUnitLabel,
} from '@/lib/format'
import type { UnitSystem } from '@/lib/format'

/**
 * Per-metric definitions for the timeline chart. Deliberately only the
 * metrics the master spec names for this view -- never invent a series for
 * a field that isn't present in the activity's records (per the "don't
 * guess/interpolate missing metrics" requirement).
 */
export interface MetricDef {
  key: 'heartRate' | 'speed' | 'cadence' | 'power' | 'altitude' | 'temperature'
  label: string
  /** The unit the value is *stored* in (FIT/SI); see `metricDisplay` for what the chart shows. */
  unit: string
}

/** How a metric is shown on the chart: its display unit and the stored-to-display conversion (`null` = shown as stored). */
export interface MetricDisplay {
  unit: string
  convert: ((value: number) => number) | null
}

/**
 * Display unit + conversion for a metric under the current unit system.
 * Speed is never shown in raw m/s (km/h or mph); altitude is m or ft.
 * Display only: the store and exports keep the stored SI values.
 */
export function metricDisplay(def: MetricDef, units: UnitSystem): MetricDisplay {
  switch (def.key) {
    case 'speed':
      return {
        unit: speedUnitLabel(units),
        convert: (v) => metersPerSecondToSpeedUnit(v, units),
      }
    case 'altitude':
      return elevationDisplay(units)
    default:
      return { unit: def.unit, convert: null }
  }
}

/** Display unit + conversion for an elevation in metres (device altitude, terrain overlay). */
export function elevationDisplay(units: UnitSystem): MetricDisplay {
  return {
    unit: elevationUnitLabel(units),
    convert: units === 'metric' ? null : (v) => metersToElevationUnit(v, units),
  }
}

export const METRIC_DEFS: readonly MetricDef[] = [
  { key: 'heartRate', label: 'Heart rate', unit: 'bpm' },
  { key: 'speed', label: 'Speed', unit: 'm/s' },
  { key: 'cadence', label: 'Cadence', unit: 'rpm' },
  { key: 'power', label: 'Power', unit: 'W' },
  { key: 'altitude', label: 'Altitude', unit: 'm' },
  { key: 'temperature', label: 'Temperature', unit: '°C' },
]

/**
 * A colorblind-safe categorical palette (Okabe & Ito, 2008) for
 * distinguishing data *series* from one another. This is a deliberate,
 * narrow exception to "never hardcode a color that bypasses the theme
 * tokens": the theme tokens (`index.css`) define *UI chrome* (background,
 * border, text), not a multi-hue data palette, and chart series need
 * several mutually-distinguishable hues regardless of light/dark mode --
 * a concern CSS variables for UI chrome don't address. Chosen for
 * reasonable contrast on both the light and dark `--card` background.
 *
 * Heart rate (red) and altitude (neutral gray) follow the conventions
 * users expect from fitness apps rather than Okabe-Ito; power moved to
 * orange so it isn't confused with the red heart-rate line.
 */
export const METRIC_COLORS: Record<MetricDef['key'], string> = {
  heartRate: '#e53935',
  speed: '#0072b2',
  cadence: '#009e73',
  power: '#e69f00',
  altitude: '#8c8c8c',
  temperature: '#56b4e9',
}

/**
 * Reads a metric's effective value off a record. For `altitude`/`speed`,
 * many real Garmin devices record the higher-precision `enhanced_altitude`/
 * `enhanced_speed` fields alongside (or instead of) the legacy 16-bit
 * `altitude`/`speed` fields -- the enhanced fields exist specifically to
 * supersede the legacy ones with better range/resolution, so when both are
 * present this prefers the enhanced value, falling back to the legacy field
 * only when no enhanced value exists. Without this, a file that only
 * populates `enhanced_altitude` (common) would show no elevation data at
 * all despite the activity clearly having it.
 */
function readMetricValue(record: ActivityRecord, key: MetricDef['key']): number | undefined {
  if (key === 'altitude' || key === 'speed') {
    const enhancedKey = key === 'altitude' ? 'enhancedAltitude' : 'enhancedSpeed'
    const enhanced = record[enhancedKey]
    if (typeof enhanced === 'number') return enhanced
  }
  const value = record[key]
  return typeof value === 'number' ? value : undefined
}

/** Metrics with at least one effective numeric value somewhere in `records`. */
export function getAvailableMetrics(records: ActivityRecord[]): MetricDef[] {
  return METRIC_DEFS.filter((def) => records.some((r) => readMetricValue(r, def.key) != null))
}

/**
 * `[epochMs, value]` pairs for one metric, one entry per record that has a
 * timestamp. A record with a timestamp but no value for this metric gets an
 * explicit `null` (a gap, not a guess) rather than being dropped entirely --
 * every metric series must share the same timestamp grid, or ECharts'
 * axis-triggered tooltip can't reliably find each series' value at a given
 * hover position once per-series sampling/downsampling no longer lines up.
 */
export function buildMetricSeriesData(
  records: ActivityRecord[],
  key: MetricDef['key'],
  /** Optional stored-to-display conversion (`MetricDisplay.convert`), applied to this display copy only. */
  convert?: ((value: number) => number) | null,
): [number, number | null][] {
  const data: [number, number | null][] = []
  for (const record of records) {
    if (!(record.timestamp instanceof Date)) continue
    const value = readMetricValue(record, key)
    data.push([
      record.timestamp.getTime(),
      value == null ? null : convert != null ? convert(value) : value,
    ])
  }
  return data
}
